import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PageSEO } from "@/components/PageSEO";
import { webpSrc, onImgError } from "@/lib/img";
import { SEO } from "@/lib/seo";
import {
  API, SERVICES, PROBLEMS, SOLUTIONS, METHOD_STEPS,
  TESTIMONIALS_DATA, LOCATIONS, SOCIAL_LINKS, WHATSAPP_NUMBER,
  CONTACT_EMAIL, TEAM_ITEMS
} from "@/lib/constants";
import {
  FileCheck, Compass, BarChart3, Phone, Mail, MapPin,
  Facebook, Instagram, Linkedin, Menu, X, Star, ArrowRight,
  CheckCircle, Users, Clock, Award, ChevronRight, MessageCircle,
  Building, Target, TrendingUp, Shield, Play, XCircle, Zap,
  Eye, Lock, AlertTriangle, Sparkles, ArrowDown, Edit, Trash2,
  Plus, Save, LogOut, Image, Download, ShoppingCart, FileSpreadsheet,
  HelpCircle, ChevronDown, CircleDollarSign, CalendarCheck, PieChart,
  FileText, Wallet, BadgePercent, Sparkle, RefreshCw, ShieldCheck, BookOpen
} from "lucide-react";

const ProductLandingPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [billingType, setBillingType] = useState('azienda');
  const [billing, setBilling] = useState({
    denominazione: '', codice_fiscale: '', partita_iva: '',
    sdi: '', pec: '', indirizzo: '', cap: '', citta: '', provincia: '', email: ''
  });
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null); // { code, discount_percent }
  const [couponChecking, setCouponChecking] = useState(false);
  const [waivedWithdrawal, setWaivedWithdrawal] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;
    setCouponChecking(true);
    try {
      const res = await axios.post(`${API}/coupons/validate`, { code, product_slug: slug });
      setCouponApplied(res.data);
      toast.success(`Codice ${res.data.code} applicato (-${res.data.discount_percent}%)`);
    } catch (e) {
      setCouponApplied(null);
      toast.error(e.response?.data?.detail || 'Codice non valido');
    } finally {
      setCouponChecking(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
  };

  useEffect(() => {
    axios.get(`${API}/products/${slug}`).then(r => {
      setProduct(r.data);
      setLoading(false);
      // Meta Pixel: eventi di funnel sul prodotto.
      // - Su /strumenti/:slug (landing) → ViewContent
      // - Su /acquista/:slug (form checkout) → InitiateCheckout
      // Le landing statiche (landing_type=static) sparano già il proprio ViewContent
      // nel loro <head> — qui evitiamo il doppio evento per quei 3 slug.
      if (typeof window.fbq === 'function') {
        try {
          const p = r.data;
          const params = {
            content_name: p.title,
            content_ids: [p.slug],
            content_type: 'product',
            value: p.price,
            currency: (p.currency || 'EUR').toUpperCase(),
          };
          const onCheckout = window.location.pathname.startsWith('/acquista/');
          const isStatic = p.landing_type === 'static';
          if (onCheckout) {
            window.fbq('track', 'InitiateCheckout', params);
          } else if (!isStatic) {
            window.fbq('track', 'ViewContent', params);
          }
        } catch (e) { /* noop */ }
      }
    }).catch(() => setLoading(false));
  }, [slug]);

  const validateBilling = () => {
    if (!billing.denominazione || !billing.codice_fiscale || !billing.indirizzo || !billing.cap || !billing.citta || !billing.provincia || !billing.email) {
      toast.error('Compila tutti i campi obbligatori');
      return false;
    }
    if (billingType === 'azienda') {
      if (!billing.partita_iva) {
        toast.error('Partita IVA obbligatoria per aziende');
        return false;
      }
      if (!billing.sdi && !billing.pec) {
        toast.error('Inserire codice SDI oppure PEC');
        return false;
      }
    }
    return true;
  };

  const handlePurchase = async () => {
    if (!validateBilling()) return;
    if (!privacyConsent) {
      toast.error('Devi accettare l\'informativa privacy per procedere');
      return;
    }
    if (!waivedWithdrawal) {
      toast.error('Devi confermare l\'esecuzione immediata per procedere al download');
      return;
    }
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/products/${slug}/purchase`, {
        coupon_code: couponApplied?.code || null,
        waived_withdrawal: true,
        privacy_consent: true,
        marketing_consent: marketingConsent,
        billing: {
          tipo: billingType, denominazione: billing.denominazione,
          codice_fiscale: billing.codice_fiscale,
          partita_iva: billing.partita_iva || null,
          sdi: billing.sdi || null, pec: billing.pec || null,
          indirizzo: billing.indirizzo, cap: billing.cap,
          citta: billing.citta, provincia: billing.provincia,
          email: billing.email,
        }
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel pagamento');
      setProcessing(false);
    }
  };

  if (loading) return (
    <main className="min-h-screen bg-[#F4EFEA] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
    </main>
  );

  if (!product) return (
    <main className="min-h-screen bg-[#F4EFEA] flex items-center justify-center p-6">
      <div className="text-center">
        <XCircle className="text-netflix mx-auto mb-4" size={48} />
        <h1 className="font-heading text-3xl text-[#0D1527] mb-4">PRODOTTO NON TROVATO</h1>
        <Link to="/strumenti" className="text-gold hover:underline">Torna agli strumenti</Link>
      </div>
    </main>
  );

  // ---- Static landings served from /public/strumenti/<slug>/index.html ----
  // Rendered React landing is used only for legacy products (landing_type !== "static").
  // On /strumenti/:slug we redirect the browser to the static HTML.
  // On /strumenti/:slug/acquista we skip the redirect and show only the checkout form.
  const isCheckoutOnly = location.pathname.startsWith('/acquista/');
  const inputClass = "w-full bg-white border border-[#D6CFC0] focus:border-gold rounded px-4 py-3 text-[#0D1527] placeholder:text-[#98A0B0] outline-none transition-colors text-sm";
  if (product.landing_type === 'static' && !isCheckoutOnly) {
    // Some products share a common landing page (e.g. CPB Report + CPB Report+call → same landing).
    // Use product.landing_slug when present, otherwise fall back to the product's own slug.
    const target = product.landing_slug || slug;
    window.location.replace(`/strumenti/${target}/`);
    return (
      <main className="min-h-screen bg-[#F4EFEA] flex items-center justify-center">
        <div className="text-[#64708A] text-sm">Caricamento…</div>
      </main>
    );
  }
  // In checkout-only mode we skip the whole landing and just present the billing form
  // inline in the page (NOT as a floating modal). The floating modal is still used when
  // the user clicks "Acquista" from the rendered landing page.
  const checkoutFormPanel = (
    <div className="bg-[#F4EFEA] border border-[#E8E2D5] rounded-lg w-full max-w-lg mx-auto">
      <div className="flex items-center justify-between p-6 border-b border-[#E8E2D5]">
        <div>
          <h3 className="font-heading text-xl text-[#0D1527]">{product.title}</h3>
          <p className="text-gold font-heading text-2xl">{product.price.toFixed(2)} EUR</p>
        </div>
        {!isCheckoutOnly && (
          <button
            type="button"
            onClick={() => setShowCheckout(false)}
            className="text-[#64708A] hover:text-[#0D1527] flex-shrink-0"
            aria-label="Chiudi"
            data-testid="checkout-close-btn"
          >
            <X size={24} />
          </button>
        )}
      </div>

      <div className="p-6 space-y-4">
        <h4 className="font-heading text-lg text-[#0D1527]">DATI DI FATTURAZIONE</h4>

        <div className="flex gap-2" data-testid="checkout-billing-type">
          <button type="button" onClick={() => { setBillingType('privato'); setBilling(b => ({...b, partita_iva:'', sdi:'', pec:''})); }}
            className={`flex-1 py-2.5 rounded text-sm font-bold transition-all ${billingType === 'privato' ? 'bg-gold text-black' : 'bg-white text-[#64708A] border border-[#E8E2D5]'}`}
            data-testid="checkout-type-privato">Privato</button>
          <button type="button" onClick={() => setBillingType('azienda')}
            className={`flex-1 py-2.5 rounded text-sm font-bold transition-all ${billingType === 'azienda' ? 'bg-gold text-black' : 'bg-white text-[#64708A] border border-[#E8E2D5]'}`}
            data-testid="checkout-type-azienda">Azienda</button>
        </div>

        <div>
          <label className="block text-xs text-[#64708A] mb-1">{billingType === 'azienda' ? 'Ragione Sociale *' : 'Nome e Cognome *'}</label>
          <input type="text" value={billing.denominazione} onChange={e => setBilling({...billing, denominazione: e.target.value})} className={inputClass} placeholder={billingType === 'azienda' ? 'Es: Mario Rossi SRL' : 'Es: Mario Rossi'} data-testid="checkout-denominazione" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-[#64708A] mb-1">Codice Fiscale *</label>
            <input type="text" value={billing.codice_fiscale} onChange={e => setBilling({...billing, codice_fiscale: e.target.value.toUpperCase()})} className={inputClass} data-testid="checkout-cf" />
          </div>
          {billingType === 'azienda' && (
            <div>
              <label className="block text-xs text-[#64708A] mb-1">P.IVA *</label>
              <input type="text" value={billing.partita_iva} onChange={e => setBilling({...billing, partita_iva: e.target.value})} className={inputClass} data-testid="checkout-piva" />
            </div>
          )}
        </div>

        {billingType === 'azienda' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[#64708A] mb-1">Codice SDI</label>
                <input type="text" value={billing.sdi} onChange={e => setBilling({...billing, sdi: e.target.value.toUpperCase()})} className={inputClass} data-testid="checkout-sdi" />
              </div>
              <div>
                <label className="block text-xs text-[#64708A] mb-1">PEC</label>
                <input type="email" value={billing.pec} onChange={e => setBilling({...billing, pec: e.target.value})} className={inputClass} data-testid="checkout-pec" />
              </div>
            </div>
            <p className="text-[#98A0B0] text-xs">Almeno uno tra SDI e PEC per la fattura elettronica</p>
          </>
        )}

        <div>
          <label className="block text-xs text-[#64708A] mb-1">Indirizzo *</label>
          <input type="text" value={billing.indirizzo} onChange={e => setBilling({...billing, indirizzo: e.target.value})} className={inputClass} data-testid="checkout-indirizzo" />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-[#64708A] mb-1">CAP *</label>
            <input type="text" value={billing.cap} onChange={e => setBilling({...billing, cap: e.target.value})} className={inputClass} data-testid="checkout-cap" />
          </div>
          <div>
            <label className="block text-xs text-[#64708A] mb-1">Citta *</label>
            <input type="text" value={billing.citta} onChange={e => setBilling({...billing, citta: e.target.value})} className={inputClass} data-testid="checkout-citta" />
          </div>
          <div>
            <label className="block text-xs text-[#64708A] mb-1">Prov. *</label>
            <input type="text" maxLength={2} value={billing.provincia} onChange={e => setBilling({...billing, provincia: e.target.value.toUpperCase()})} className={inputClass} data-testid="checkout-provincia" />
          </div>
        </div>

        <div>
          <label className="block text-xs text-[#64708A] mb-1">Email *</label>
          <input type="email" value={billing.email} onChange={e => setBilling({...billing, email: e.target.value})} className={inputClass} data-testid="checkout-email" />
        </div>

        {/* Coupon */}
        <div className="pt-3 mt-2 border-t border-[#E8E2D5]">
          <label className="block text-xs text-[#64708A] mb-2">Hai un codice sconto?</label>
          {!couponApplied ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={e => setCouponCode(e.target.value.toUpperCase())}
                className={`${inputClass} flex-1`}
                placeholder="Es: OMAGGIO"
                data-testid="checkout-coupon-input"
              />
              <button
                type="button"
                onClick={applyCoupon}
                disabled={couponChecking || !couponCode.trim()}
                className="px-4 py-3 rounded text-sm font-bold bg-[#EDE6DA] text-[#0D1527] hover:bg-white/20 disabled:opacity-40 transition-colors"
                data-testid="checkout-coupon-apply-btn"
              >
                {couponChecking ? '...' : 'Applica'}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded px-3 py-2.5" data-testid="checkout-coupon-applied">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-green-400" />
                <span className="text-[#0D1527] font-semibold text-sm">{couponApplied.code}</span>
                <span className="text-green-400 text-xs">-{couponApplied.discount_percent}%</span>
              </div>
              <button onClick={removeCoupon} className="text-[#8B94A6] hover:text-[#0D1527] text-xs" data-testid="checkout-coupon-remove-btn">Rimuovi</button>
            </div>
          )}
        </div>
      </div>

      <div className="p-6 border-t border-[#E8E2D5]">
        {couponApplied && (
          <div className="mb-4 flex items-center justify-between text-sm" data-testid="checkout-price-summary">
            <span className="text-[#4A5568]">Totale</span>
            <div className="text-right">
              <span className="text-[#8B94A6] line-through mr-2">{product.price.toFixed(2)} €</span>
              <span className="font-heading text-2xl text-gold">
                {(product.price * (100 - couponApplied.discount_percent) / 100).toFixed(2)} €
              </span>
            </div>
          </div>
        )}
        <label className="flex items-start gap-2 mb-2 cursor-pointer select-none" data-testid="checkout-privacy-label">
          <input
            type="checkbox"
            checked={privacyConsent}
            onChange={(e) => setPrivacyConsent(e.target.checked)}
            className="mt-0.5 accent-gold flex-shrink-0"
            data-testid="checkout-privacy-checkbox"
          />
          <span className="text-[#64708A] text-[11px] leading-snug">
            Dichiaro di aver letto l'<a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">informativa privacy</a> e acconsento al trattamento dei miei dati per l'esecuzione del contratto e per gli obblighi fiscali connessi (art. 6.1.b e 6.1.c GDPR). *
          </span>
        </label>
        <label className="flex items-start gap-2 mb-2 cursor-pointer select-none" data-testid="checkout-marketing-label">
          <input
            type="checkbox"
            checked={marketingConsent}
            onChange={(e) => setMarketingConsent(e.target.checked)}
            className="mt-0.5 accent-gold flex-shrink-0"
            data-testid="checkout-marketing-checkbox"
          />
          <span className="text-[#64708A] text-[11px] leading-snug">
            Acconsento facoltativamente a ricevere comunicazioni commerciali dello studio (nuovi strumenti, aggiornamenti fiscali, offerte riservate). Posso revocare il consenso in qualsiasi momento (art. 6.1.a GDPR). Facoltativo.
          </span>
        </label>
        <label className="flex items-start gap-2 mb-3 cursor-pointer select-none" data-testid="checkout-waiver-label">
          <input
            type="checkbox"
            checked={waivedWithdrawal}
            onChange={(e) => setWaivedWithdrawal(e.target.checked)}
            className="mt-0.5 accent-gold flex-shrink-0"
            data-testid="checkout-waiver-checkbox"
          />
          <span className="text-[#64708A] text-[11px] leading-snug">
            Chiedo espressamente che il contenuto digitale mi venga reso disponibile per il download immediato dopo il pagamento e riconosco che, avviato il download, perderò il diritto di recesso ai sensi dell'art. 59 c. 1 lett. o) D.Lgs. 206/2005. *
          </span>
        </label>
        <button
          onClick={handlePurchase}
          disabled={processing || !waivedWithdrawal || !privacyConsent}
          className="w-full flex items-center justify-center gap-3 bg-gold text-black hover:bg-[#96742F] py-4 rounded-lg font-bold text-lg transition-all disabled:opacity-50"
          data-testid="checkout-pay-btn"
        >
          {processing
            ? (couponApplied?.discount_percent === 100 ? 'Conferma in corso...' : 'Reindirizzamento a Stripe...')
            : (couponApplied?.discount_percent === 100
                ? 'CONFERMA OMAGGIO GRATUITO'
                : `Paga ${(product.price * (100 - (couponApplied?.discount_percent || 0)) / 100).toFixed(2)} EUR con Carta`)
          }
        </button>
        <p className="text-center text-[#98A0B0] text-xs mt-3">
          {couponApplied?.discount_percent === 100 ? 'Riceverai subito il link al file' : 'Pagamento sicuro gestito da Stripe'}
        </p>
      </div>
    </div>
  );

  const featureIcons = [PieChart, CircleDollarSign, CalendarCheck, FileSpreadsheet, Building, Clock, CheckCircle];

  // Helper: render rich text with simple tags
  const renderRich = (str) => {
    if (!str) return null;
    const parts = str.split(/(<gold>.*?<\/gold>|<netflix>.*?<\/netflix>|<strong>.*?<\/strong>|<br\s*\/?>)/g);
    return parts.map((p, i) => {
      if (p.startsWith('<gold>')) return <span key={i} className="text-gold">{p.slice(6, -7)}</span>;
      if (p.startsWith('<netflix>')) return <span key={i} className="text-netflix">{p.slice(9, -10)}</span>;
      if (p.startsWith('<strong>')) return <strong key={i} className="text-[#0D1527]">{p.slice(8, -9)}</strong>;
      if (/^<br\s*\/?>$/.test(p)) return <br key={i} />;
      return p;
    });
  };
  const ICON_MAP = { Users, Building, Target };

  // Read landing content from product with fallbacks (Gestore Forfettario style by default)
  const heroImage = product.hero_image || product.image_url || '/images/gestore-forfettario/19_full_dashboard_annuale.png';
  const heroH1 = product.hero_promise_h1 || 'Il foglio Excel che <gold>fa il lavoro per te.</gold>';
  const heroSub = product.hero_subtitle || product.short_description || '';
  const pillars = product.tagline_pillars || ['Stima automatica tasse e contributi','Dashboard mensile e annuale','Calendario scadenze F24 + INPS','Gestione multi-conto e carte'];
  const painLabel = product.pain_intro_label || 'SE SEI INTERESSATO AL CONTROLLO DI GESTIONE...';
  const painTitle = product.pain_intro_title || '...quante di queste cose <netflix>ti tolgono il sonno?</netflix>';
  const painPoints = product.pain_points || [];
  const insideTitle = product.inside_title || 'Tutto quello che vedi qui sotto <gold>è dentro l\'Excel.</gold>';
  const insideSubtitle = product.inside_subtitle || '';
  const mainSections = product.main_sections || [];
  const galleryLabel = product.gallery_label || 'E INOLTRE NEL FILE...';
  const galleryItems = product.gallery || [];
  const comparisonRows = product.comparison_rows || [];
  const compTitleA = product.comp_title || 'Excel generico<br/>(gratis da internet)';
  const compTitleB = product.comp_title_b || 'Gestore Forfettario PRO<br/>(creato da commercialista)';
  const targetLabel = product.target_label || 'Se sei <gold>forfettario</gold>, è per te.';
  const targetAudience = product.target_audience || [];
  const testimonials = product.testimonials || [];
  const downloadFilename = product.download_filename || 'Strumento.xlsx';
  const fileExt = (downloadFilename.split('.').pop() || 'xlsx').toLowerCase();

  return (
    <>
    {isCheckoutOnly ? (
      <main className="bg-[#F4EFEA] min-h-screen pt-24 pb-24" data-testid="product-checkout-page">
        <PageSEO title={`Acquisto: ${product.title} | SC Dottori Commercialisti`} description={`Completa l'acquisto di ${product.title}`} />
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link
            to={`/strumenti/${product.landing_slug || slug}/`}
            reloadDocument
            className="inline-flex items-center gap-2 text-[#64708A] hover:text-gold text-xs tracking-[0.2em] font-bold transition-colors group mb-8"
            data-testid="checkout-back-to-product"
          >
            <ArrowRight size={14} className="rotate-180 transition-transform group-hover:-translate-x-1" />
            TORNA ALLA SCHEDA PRODOTTO
          </Link>
          <div className="max-w-2xl mb-8">
            <p className="text-gold text-xs tracking-[0.3em] font-bold mb-4">COMPLETA IL TUO ACQUISTO</p>
            <h1 className="font-heading text-3xl sm:text-4xl text-[#0D1527] leading-tight mb-3">{product.title}</h1>
            <p className="text-gold font-heading text-3xl mb-2">{product.price.toFixed(2)} EUR</p>
            <p className="text-[#64708A] text-sm">Inserisci i dati di fatturazione. Il pagamento è gestito da Stripe; il download parte immediatamente dopo il pagamento.</p>
          </div>
          {checkoutFormPanel}
        </div>
      </main>
    ) : (
    <main className="bg-[#F4EFEA] pb-28 lg:pb-20" data-testid="product-landing-page">
      <PageSEO title={`${product.title} | SC Dottori Commercialisti`} description={product.short_description} />

      {/* ====== BREADCRUMB ====== */}
      <div className="pt-24 pb-2">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link
            to="/strumenti"
            className="inline-flex items-center gap-2 text-[#64708A] hover:text-gold text-xs tracking-[0.2em] font-bold transition-colors group"
            data-testid="back-to-strumenti"
          >
            <ArrowRight size={14} className="rotate-180 transition-transform group-hover:-translate-x-1" />
            TORNA A TUTTI GLI STRUMENTI
          </Link>
        </div>
      </div>

      {/* ====== HERO ====== */}
      <section className="relative pt-8 pb-16 overflow-hidden">
        {/* Background grid pattern */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{backgroundImage:'linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)', backgroundSize:'48px 48px'}} />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-[1.05fr_1fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#F7F0E1] border border-gold/30 px-3 py-1.5 rounded-full mb-6">
              <Sparkle size={12} className="text-gold" />
              <span className="text-gold text-[11px] tracking-[0.25em] font-bold">CREATO DA UN COMMERCIALISTA</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0D1527] mb-6 leading-[1.05]" data-testid="hero-title">
              {renderRich(heroH1)}
            </h1>
            <p className="text-lg text-[#4A5568] mb-3 leading-relaxed">
              {renderRich(heroSub)}
            </p>
            <p className="text-[#64708A] mb-8 text-sm">
              Niente abbonamenti. Niente cloud. Solo il tuo Excel, sul tuo PC, sotto il tuo controllo.
            </p>

            {/* Trust bullets */}
            <ul className="grid sm:grid-cols-2 gap-2.5 mb-8">
              {pillars.map((t,i) => (
                <li key={i} className="flex items-center gap-2 text-[#3B4658] text-sm">
                  <CheckCircle size={16} className="text-gold flex-shrink-0" /> {t}
                </li>
              ))}
            </ul>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="leading-tight">
                <div className="flex items-baseline gap-2">
                  <span className="font-heading text-5xl text-gold">{product.price.toFixed(2)}</span>
                  <span className="text-gold text-xl">EUR</span>
                </div>
                <p className="text-[#8B94A6] text-xs mt-1">Acquisto unico — tuo per sempre</p>
              </div>
              <button
                onClick={() => setShowCheckout(true)}
                className="group flex items-center gap-3 bg-gold text-black hover:bg-[#96742F] px-8 py-4 rounded font-bold text-lg transition-all hover:scale-[1.03] shadow-[0_0_40px_-12px_rgba(212,175,55,0.6)]"
                data-testid="buy-now-btn"
              >
                <ShoppingCart size={20} /> ACQUISTA ORA
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
            </div>
            <p className="text-[#8B94A6] text-xs mt-4 flex items-center gap-2">
              <ShieldCheck size={14} className="text-gold" /> Pagamento sicuro Stripe — Carta, Apple/Google Pay
            </p>
          </div>

          {/* Hero mockup: Excel screenshot in a "browser/window" frame */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-tr from-gold/20 via-transparent to-netflix/10 blur-2xl pointer-events-none" />
            <div className="relative bg-[#F4EFEA] border border-[#E8E2D5] rounded-xl overflow-hidden shadow-2xl">
              {/* window chrome */}
              <div className="flex items-center gap-1.5 px-4 py-3 bg-[#111] border-b border-[#EFE9DE]">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                <span className="w-3 h-3 rounded-full bg-[#28c840]" />
                <span className="ml-3 text-[#8B94A6] text-xs font-mono">{downloadFilename}</span>
              </div>
              <img
                src={webpSrc(heroImage)}
                onError={onImgError(heroImage)}
                alt={product.title}
                decoding="async"
                className="w-full block"
              />
            </div>
            {/* Floating badge: bonus PDF */}
            <div className="hidden sm:flex absolute -bottom-4 -left-4 lg:-left-8 items-center gap-2 bg-netflix text-[#0D1527] px-4 py-2.5 rounded-lg shadow-xl rotate-[-3deg]">
              <BookOpen size={18} />
              <div className="text-left leading-tight">
                <p className="text-[10px] tracking-widest font-bold opacity-80">INCLUSO</p>
                <p className="text-sm font-bold">Manuale PDF passo-passo</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ====== STAT STRIP (optional, only if product.stats provided) ====== */}
      {product.stats && product.stats.length > 0 && (
        <section className="border-y border-gold/20 bg-gradient-to-r from-gold/5 via-transparent to-gold/5 py-10 mb-16">
          <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-4" data-testid="stat-strip">
            {product.stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="font-heading text-4xl sm:text-5xl text-gold mb-1 leading-none">{s.value}</div>
                <div className="text-[#64708A] text-xs sm:text-sm tracking-wider uppercase">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ====== 3 PRICE TIERS / TRAFFIC LIGHT (optional, only if product.price_tiers provided) ====== */}
      {product.price_tiers && product.price_tiers.length === 3 && (
        <section className="max-w-6xl mx-auto px-6 mb-24" data-testid="price-tiers-section">
          <div className="text-center mb-12">
            <p className="text-gold text-xs tracking-[0.3em] font-bold mb-3">{product.price_tiers_label || 'IL CUORE DEL METODO'}</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0D1527]">
              {renderRich(product.price_tiers_title || 'Tre prezzi. Tre <gold>strategie diverse.</gold>')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {product.price_tiers.map((t, i) => {
              const colors = [
                { dot: 'bg-red-500', bg: 'bg-red-500/5', border: 'border-red-500/30', text: 'text-red-400' },
                { dot: 'bg-yellow-400', bg: 'bg-yellow-400/5', border: 'border-yellow-400/30', text: 'text-yellow-300' },
                { dot: 'bg-emerald-400', bg: 'bg-emerald-400/5', border: 'border-emerald-400/30', text: 'text-emerald-300' },
              ];
              const c = colors[i] || colors[1];
              return (
                <div key={i} className={`relative ${c.bg} border ${c.border} rounded-xl p-7 overflow-hidden`}>
                  <div className="flex items-center gap-3 mb-5">
                    <span className={`w-3 h-3 rounded-full ${c.dot} shadow-lg`} style={{boxShadow: `0 0 18px currentColor`}} />
                    <span className={`text-[10px] tracking-[0.25em] font-bold ${c.text}`}>{t.tag}</span>
                  </div>
                  <h3 className="font-heading text-2xl text-[#0D1527] mb-3 leading-tight">{t.title}</h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed mb-5">{t.desc}</p>
                  <p className={`text-xs font-mono ${c.text} pt-4 border-t border-[#EFE9DE]`}>{t.example}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ====== PAIN POINTS ====== */}
      <section className="bg-white border-y border-[#EFE9DE] py-20 mb-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-netflix text-xs tracking-[0.3em] font-bold mb-3">{painLabel}</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0D1527] leading-tight">
              {renderRich(painTitle)}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {painPoints.map((p, i) => (
              <div key={i} className="flex gap-4 bg-white border border-[#E8E2D5] rounded-lg p-5 hover:border-netflix/30 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-netflix/10 border border-netflix/30 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle size={18} className="text-netflix" />
                </div>
                <div>
                  <p className="text-[#0D1527] font-semibold mb-1">{p.t}</p>
                  <p className="text-[#64708A] text-sm">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-[#4A5568] mt-10 text-lg italic">
            Il <span className="text-gold not-italic font-bold">{product.title}</span> è nato per risolvere esattamente questo.
          </p>
        </div>
      </section>

      {/* ====== COSA CONTIENE IL FILE ====== */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 mb-24">
        <div className="text-center mb-14">
          <p className="text-gold text-xs tracking-[0.3em] font-bold mb-4">DENTRO IL FILE</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#0D1527] mb-3">
            {renderRich(insideTitle)}
          </h2>
          {insideSubtitle && <p className="text-[#4A5568] max-w-2xl mx-auto">{insideSubtitle}</p>}
        </div>

        <div className="space-y-12">
          {mainSections.map((b, i) => (
            <div key={i} className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${b.reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}>
              <div>
                <p className="text-gold text-xs tracking-[0.3em] font-bold mb-3">{b.tag}</p>
                <h3 className="font-heading text-2xl sm:text-3xl text-[#0D1527] mb-4 leading-tight">{b.title}</h3>
                <p className="text-[#4A5568] mb-6 leading-relaxed">{b.desc}</p>
                <ul className="space-y-2.5">
                  {(b.bullets || []).map((bl, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-[#3B4658] text-sm">
                      <CheckCircle size={16} className="text-gold flex-shrink-0 mt-0.5" /> {bl}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#F4EFEA] border border-[#E8E2D5] rounded-xl overflow-hidden shadow-2xl">
                <div className="flex items-center gap-1.5 px-3 py-2 bg-[#111] border-b border-[#EFE9DE]">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                </div>
                <img src={webpSrc(b.img)} onError={onImgError(b.img)} alt={b.title} className="w-full block" loading="lazy" decoding="async" />
              </div>
            </div>
          ))}
        </div>

        {/* Gallery */}
        {galleryItems.length > 0 && (
          <div className="mt-16">
            <p className="text-center text-[#8B94A6] text-xs tracking-[0.3em] font-bold mb-8">{galleryLabel}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {galleryItems.map((g, i) => (
                <div key={i} className="bg-[#F4EFEA] border border-[#E8E2D5] rounded-lg overflow-hidden hover:border-gold/30 transition-colors group">
                  <div className="aspect-[4/3] overflow-hidden bg-white">
                    <img src={webpSrc(g.img)} onError={onImgError(g.img)} alt={g.t} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                  </div>
                  <div className="p-4">
                    <p className="text-[#0D1527] font-semibold text-sm mb-1">{g.t}</p>
                    <p className="text-[#64708A] text-xs">{g.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ====== COSA RICEVI ====== */}
      <section className="bg-gradient-to-b from-gold/5 to-transparent border-y border-gold/20 py-20 mb-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-gold text-xs tracking-[0.3em] font-bold mb-3">COSA TI ARRIVA SUBITO DOPO IL PAGAMENTO</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0D1527]">Due file. Zero abbonamenti.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#F4EFEA] border border-gold/30 rounded-xl p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-gold text-black text-[10px] font-bold px-2 py-1 rounded tracking-widest">PRINCIPALE</div>
              <FileSpreadsheet className="text-gold mb-4" size={40} />
              <h3 className="font-heading text-xl text-[#0D1527] mb-2">{product.title}</h3>
              <p className="text-[#64708A] text-sm mb-4">Il file Excel completo (.{fileExt})</p>
              <ul className="space-y-2 text-sm text-[#4A5568]">
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-gold" /> Moduli integrati con formule pronte</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-gold" /> Tabelle, riepiloghi e grafici automatici</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-gold" /> Compatibile Excel 365 / 2021+</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-gold" /> Personalizzabile con i tuoi dati</li>
              </ul>
            </div>
            <div className="bg-[#F4EFEA] border border-[#E8E2D5] rounded-xl p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-netflix text-[#0D1527] text-[10px] font-bold px-2 py-1 rounded tracking-widest">INCLUSO</div>
              <BookOpen className="text-netflix mb-4" size={40} />
              <h3 className="font-heading text-xl text-[#0D1527] mb-2">Manuale d'uso (PDF)</h3>
              <p className="text-[#64708A] text-sm mb-4">La guida passo-passo per partire in 10 minuti</p>
              <ul className="space-y-2 text-sm text-[#4A5568]">
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-gold" /> Come compilare ogni scheda</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-gold" /> Significato fiscale di ogni voce</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-gold" /> Riferimenti normativi essenziali</li>
                <li className="flex items-center gap-2"><CheckCircle size={14} className="text-gold" /> Best practice da commercialista</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ====== COME FUNZIONA ====== */}
      <section className="max-w-5xl mx-auto px-6 mb-24">
        <div className="text-center mb-12">
          <p className="text-gold text-xs tracking-[0.3em] font-bold mb-3">COME FUNZIONA</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#0D1527]">Operativo in <span className="text-gold">10 minuti.</span></h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: '01', icon: ShoppingCart, t: 'Acquisti online', d: 'Pagamento sicuro Stripe con carta, Apple Pay o Google Pay. Fattura emessa automaticamente con i tuoi dati.' },
            { n: '02', icon: Download, t: 'Scarichi i due file', d: 'Subito dopo il pagamento ricevi il link per scaricare l\'Excel e il manuale PDF. Una copia ti arriva anche via email.' },
            { n: '03', icon: Sparkle, t: 'Personalizzi e parti', d: 'Inserisci nome, attività, conti correnti. Apri il manuale, segui i passaggi. In 10 minuti hai il tuo controllo di gestione.' },
          ].map((s, i) => (
            <div key={i} className="relative bg-white border border-[#E8E2D5] rounded-xl p-6">
              <span className="absolute -top-3 left-6 bg-gold text-black text-xs font-bold px-3 py-1 rounded font-mono">{s.n}</span>
              <s.icon className="text-gold mb-4 mt-2" size={28} />
              <h3 className="font-heading text-lg text-[#0D1527] mb-2">{s.t}</h3>
              <p className="text-[#4A5568] text-sm">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ====== VS FOGLIO GENERICO ====== */}
      <section className="bg-white border-y border-[#EFE9DE] py-20 mb-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-gold text-xs tracking-[0.3em] font-bold mb-3">PERCHÉ NON UN EXCEL QUALSIASI</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0D1527]">La differenza si vede <span className="text-gold">subito.</span></h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E8E2D5]">
                  <th className="text-left py-4 text-[#8B94A6] font-normal"></th>
                  <th className="text-center py-4 text-[#64708A] font-semibold" dangerouslySetInnerHTML={{__html: compTitleA}} />
                  <th className="text-center py-4 text-gold font-bold" dangerouslySetInnerHTML={{__html: compTitleB}} />
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map(([label, a, b], i) => (
                  <tr key={i} className="border-b border-[#EFE9DE]">
                    <td className="py-3 text-[#3B4658]">{label}</td>
                    <td className="text-center">
                      {a ? <CheckCircle size={18} className="text-green-400 inline" /> : <XCircle size={18} className="text-[#A6ADBB] inline" />}
                    </td>
                    <td className="text-center bg-[#FAF6EE]">
                      {b ? <CheckCircle size={18} className="text-gold inline" /> : <XCircle size={18} className="text-[#A6ADBB] inline" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ====== PER CHI E PENSATO ====== */}
      <section className="max-w-5xl mx-auto px-6 mb-24">
        <div className="text-center mb-12">
          <p className="text-gold text-xs tracking-[0.3em] font-bold mb-3">PER CHI È PENSATO</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#0D1527]">{renderRich(targetLabel)}</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          {targetAudience.map((item, i) => {
            const Icon = ICON_MAP[item.icon] || Target;
            return (
              <div key={i} className="text-center p-6 bg-white border border-[#E8E2D5] rounded-xl">
                <Icon className="text-gold mx-auto mb-3" size={32} />
                <h3 className="font-heading text-lg text-[#0D1527] mb-2">{item.t}</h3>
                <p className="text-[#64708A] text-sm">{item.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ====== TESTIMONIANZE ====== */}
      <section className="max-w-6xl mx-auto px-6 mb-24">
        <div className="text-center mb-12">
          <p className="text-gold text-xs tracking-[0.3em] font-bold mb-3">PARERI DI CHI LO USA</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#0D1527]">{renderRich(product.testimonials_title || 'Cosa dicono <gold>gli utilizzatori</gold> del file.')}</h2>
          <p className="text-[#8B94A6] text-xs mt-3 max-w-2xl mx-auto italic">
            Opinioni raccolte dai clienti dello studio. I nomi sono riportati con le sole iniziali per riservatezza.
            Le esperienze descritte sono personali e non costituiscono promessa di risultato.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5" data-testid="testimonials-section">
          {testimonials.map((t, i) => (
            <figure key={i} className="bg-white border border-[#E8E2D5] rounded-xl p-6 flex flex-col" data-testid={`testimonial-${i}`}>
              <div className="text-gold text-3xl font-heading leading-none mb-3 opacity-40">"</div>
              <blockquote className="text-[#3B4658] text-sm leading-relaxed flex-1">{t.text}</blockquote>
              <figcaption className="mt-5 pt-4 border-t border-[#EFE9DE]">
                <p className="text-[#0D1527] font-semibold text-sm">{t.author}</p>
                <p className="text-[#8B94A6] text-xs">{t.role} · {t.city}</p>
              </figcaption>
            </figure>
          ))}
        </div>

        <p className="text-center text-[#98A0B0] text-[11px] mt-8 max-w-3xl mx-auto leading-relaxed">
          Le testimonianze sono raccolte da clienti dello studio in conformità alla normativa applicabile e al Codice deontologico
          dei Dottori Commercialisti. Non rappresentano una garanzia di analogo risultato e non costituiscono attività promozionale
          delle competenze professionali dello studio.
        </p>
      </section>

      {/* ====== GARANZIA ====== */}
      <section className="max-w-3xl mx-auto px-6 mb-24">
        <div className="bg-gradient-to-br from-gold/10 via-transparent to-transparent border border-gold/30 rounded-2xl p-8 sm:p-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F7F0E1] border border-gold/30 mb-5">
            <ShieldCheck size={32} className="text-gold" />
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#0D1527] mb-3">Garanzia "Tutto chiaro o ti rimborso"</h2>
          <p className="text-[#4A5568] leading-relaxed max-w-xl mx-auto">
            Apri il file, leggi il manuale e prova a usarlo per <strong className="text-[#0D1527]">14 giorni</strong>. Se non è quello che ti aspettavi,
            scrivimi a <a href="mailto:assistenza@casalisilvia.com" className="text-gold hover:underline">assistenza@casalisilvia.com</a> e ti rimborso l'intero importo. Senza fare domande.
          </p>
        </div>
      </section>

      {/* ====== FAQ ====== */}
      {product.faq && product.faq.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 mb-24">
          <div className="text-center mb-10">
            <p className="text-gold text-xs tracking-[0.3em] font-bold mb-3">DOMANDE FREQUENTI</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0D1527]">Domande frequenti sul file.</h2>
          </div>
          <div className="space-y-3" data-testid="faq-section">
            {product.faq.map((item, i) => (
              <div key={i} className="bg-white border border-[#E8E2D5] rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white transition-colors"
                  data-testid={`faq-${i}`}
                >
                  <span className="text-[#0D1527] font-medium pr-4">{item.q}</span>
                  <ChevronDown className={`text-gold flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} size={20} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-[#4A5568] text-sm border-t border-[#EFE9DE] pt-4">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ====== FINAL CTA ====== */}
      <section className="max-w-4xl mx-auto px-6 mb-12">
        <div className="relative overflow-hidden bg-[#FAF6EE] border border-gold/40 rounded-2xl p-10 sm:p-14 text-center">
          <div className="absolute inset-0 opacity-[0.05]" style={{backgroundImage:'radial-gradient(circle at 1px 1px, #D4AF37 1px, transparent 0)', backgroundSize:'24px 24px'}} />
          <div className="relative">
            <p className="text-gold text-xs tracking-[0.3em] font-bold mb-3">UN ULTIMO PASSO</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0D1527] mb-4 leading-tight">
              {renderRich(product.final_cta_title || 'Pronto a partire?<br/><gold>Il file ti aspetta.</gold>')}
            </h2>
            <p className="text-[#4A5568] mb-8 max-w-xl mx-auto">Acquisto unico, {product.price.toFixed(2).replace('.', ',')} €. Nessun abbonamento, nessun rinnovo automatico. È tuo per sempre.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
              <div>
                <span className="font-heading text-5xl text-gold">{product.price.toFixed(2)}</span>
                <span className="text-gold text-xl ml-1">EUR</span>
              </div>
              <button
                onClick={() => setShowCheckout(true)}
                className="flex items-center gap-3 bg-gold text-black hover:bg-[#96742F] px-10 py-4 rounded font-bold text-lg transition-all hover:scale-[1.03] shadow-[0_0_40px_-12px_rgba(212,175,55,0.6)]"
                data-testid="buy-now-btn-bottom"
              >
                <ShoppingCart size={20} /> ACQUISTA ORA
              </button>
            </div>
            <p className="text-[#8B94A6] text-xs mt-6 flex items-center justify-center gap-2">
              <ShieldCheck size={14} className="text-gold" /> Pagamento sicuro Stripe · Garanzia 14 giorni
            </p>
          </div>
        </div>
      </section>
    </main>
    )}

      {/* ====== STICKY MOBILE BAR (outside <main> so position:fixed works on iOS) ====== */}
      {!isCheckoutOnly && (
      <div
        className="lg:hidden fixed inset-x-0 z-40 bg-[#F4EFEA] border-t border-gold/30 px-4 py-3 flex items-center justify-between gap-3"
        style={{ bottom: 0, paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
        data-testid="sticky-buy-bar"
      >
        <div className="leading-tight min-w-0 flex-1">
          <p className="text-[#64708A] text-[10px] uppercase tracking-wider truncate">{product.title}</p>
          <p className="text-gold font-heading text-2xl">{product.price.toFixed(2)} €</p>
        </div>
        <button
          onClick={() => setShowCheckout(true)}
          className="flex items-center gap-2 bg-gold text-black px-5 py-3 rounded font-bold text-sm flex-shrink-0"
          data-testid="sticky-buy-btn"
        >
          <ShoppingCart size={16} /> ACQUISTA
        </button>
      </div>
      )}

      {/* Floating checkout modal — only for the non-checkout-only flow (Acquista click from landing) */}
      {showCheckout && !isCheckoutOnly && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 py-8 sm:py-12 overflow-y-auto overscroll-contain" data-testid="checkout-modal">
          {checkoutFormPanel}
        </div>
      )}
    </>
  );
};


export { ProductLandingPage };
